export const GUEST_PROMOTION_FLOW_ID = 'guest-promotion';

/**
 * Exact BYOS contract from descope-iac/flows/guest-promotion.json at
 * blstrco/descope-iac@3adc6dd. These identifiers are intentionally
 * human-readable; they are the deployed Descope IDs, not placeholders.
 */
export enum GuestPromotionScreenNames {
  REGISTER = 'Guest Promotion Register',
  VERIFY_OTP = 'Guest Promotion Verify OTP',
  CHOOSE_USERNAME_EMAIL_OTP = 'Guest Promotion Choose Username',
  SOCIAL_SIGNUP_USERNAME = 'Guest Promotion Social Signup Username',
  HANDLE_VALIDATION_FAILED = 'Guest Promotion Handle Validation Failed',
  INVALID_FLOW = 'Guest Promotion Invalid Flow',
}

export const GUEST_PROMOTION_SCREEN_IDS = {
  REGISTER: 'SCGuestPromotionRegister',
  VERIFY_OTP: 'SCGuestPromotionVerifyOtp',
  CHOOSE_USERNAME_EMAIL_OTP: 'SCGuestPromotionChooseUsername',
  SOCIAL_SIGNUP_USERNAME: 'SCGuestPromotionSocialSignupUsername',
  HANDLE_VALIDATION_FAILED: 'SCGuestPromotionHandleValidationFailed',
  INVALID_FLOW: 'SCGuestPromotionInvalidFlow',
} as const;

export const GUEST_PROMOTION_INTERACTION_IDS = {
  SUBMIT_EMAIL: 'guestPromotionSubmitEmail',
  CONTINUE_GOOGLE: 'guestPromotionContinueGoogle',
  CONTINUE_APPLE: 'guestPromotionContinueApple',
  CANCEL_REGISTRATION: 'guestPromotionCancelRegistration',
  SUBMIT_CODE: 'oneTimeCodeId',
  RESEND: 'resend',
  USE_DIFFERENT_EMAIL: 'guestPromotionUseDifferentEmail',
  CANCEL_VERIFY_OTP: 'guestPromotionCancelVerifyOtp',
  SUBMIT_USERNAME: 'guestPromotionSubmitUsername',
  VALIDATE_USERNAME: 'guestPromotionValidateUsername',
  BACK_FROM_USERNAME: 'guestPromotionBackFromUsername',
  SUBMIT_SOCIAL_USERNAME: 'guestPromotionSubmitSocialUsername',
  VALIDATE_SOCIAL_USERNAME: 'guestPromotionValidateSocialUsername',
  RETRY: 'guestPromotionRetry',
  CANCEL_ERROR: 'guestPromotionCancelError',
} as const;

const GUEST_PROMOTION_REGISTER_SCREEN_NAMES = [
  GuestPromotionScreenNames.REGISTER,
] as const;

const GUEST_PROMOTION_VERIFY_OTP_SCREEN_NAMES = [
  GuestPromotionScreenNames.VERIFY_OTP,
] as const;

const GUEST_PROMOTION_CHOOSE_USERNAME_SCREEN_NAMES = [
  GuestPromotionScreenNames.CHOOSE_USERNAME_EMAIL_OTP,
] as const;

const GUEST_PROMOTION_SOCIAL_USERNAME_SCREEN_NAMES = [
  GuestPromotionScreenNames.SOCIAL_SIGNUP_USERNAME,
] as const;

const GUEST_PROMOTION_RETRY_SCREEN_NAMES = [
  GuestPromotionScreenNames.HANDLE_VALIDATION_FAILED,
  GuestPromotionScreenNames.INVALID_FLOW,
] as const;

export const GUEST_PROMOTION_CUSTOM_SCREEN_NAMES = [
  ...GUEST_PROMOTION_REGISTER_SCREEN_NAMES,
  ...GUEST_PROMOTION_VERIFY_OTP_SCREEN_NAMES,
  ...GUEST_PROMOTION_CHOOSE_USERNAME_SCREEN_NAMES,
  ...GUEST_PROMOTION_SOCIAL_USERNAME_SCREEN_NAMES,
  ...GUEST_PROMOTION_RETRY_SCREEN_NAMES,
] as const;

export type GuestPromotionScreenName =
  (typeof GUEST_PROMOTION_CUSTOM_SCREEN_NAMES)[number];

const includesScreenName = (
  screenNames: readonly string[],
  screenName?: string
): boolean => Boolean(screenName && screenNames.includes(screenName));

export const isGuestPromotionRegisterScreen = (screenName?: string): boolean =>
  includesScreenName(GUEST_PROMOTION_REGISTER_SCREEN_NAMES, screenName);

export const isGuestPromotionVerifyOtpScreen = (screenName?: string): boolean =>
  includesScreenName(GUEST_PROMOTION_VERIFY_OTP_SCREEN_NAMES, screenName);

export const isGuestPromotionChooseUsernameScreen = (
  screenName?: string
): boolean =>
  includesScreenName(GUEST_PROMOTION_CHOOSE_USERNAME_SCREEN_NAMES, screenName);

export const isGuestPromotionSocialUsernameScreen = (
  screenName?: string
): boolean =>
  includesScreenName(GUEST_PROMOTION_SOCIAL_USERNAME_SCREEN_NAMES, screenName);

export const isGuestPromotionRetryScreen = (screenName?: string): boolean =>
  includesScreenName(GUEST_PROMOTION_RETRY_SCREEN_NAMES, screenName);

export const isGuestPromotionScreen = (
  screenName: string
): screenName is GuestPromotionScreenName =>
  GUEST_PROMOTION_CUSTOM_SCREEN_NAMES.includes(
    screenName as GuestPromotionScreenName
  );
